import { useEffect, useMemo, useState } from "react";
import { Badge, Calendar, CalendarProps, ConfigProvider, Divider, Empty, List, Select, Space } from "antd";
import dayjs from "dayjs";
import { useRequest } from "ahooks";
import chroma from "chroma-js";
import { getCalendars, getSchedules } from "@/services";
import { scheduleUtil } from "@/utils";
import { calendarConfig } from "@/config";
import { ScheduleListRefreshEventName, SchedulePopover, ThSpinWithEmpty } from "@/components";

export const MiniSchedule: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<string>(dayjs().format('YYYY-MM-DD')); // YYYY-MM-DD

  const [currentMonth, setCurrentMonth] = useState<string>(dayjs().format('YYYY-MM')); // YYYY-MM

  const scheduleTimeRange = useMemo(() => {
    if (!currentMonth) return;
    return scheduleUtil.getCalendarMonthUnixes(currentMonth);
  }, [currentMonth]);

  const { data: calendars } = useRequest(getCalendars, {
    cacheKey: 'Calendars',
    debounceWait: 300,
  });

  const { data, loading, refresh } = useRequest(
    async () => {
      if (scheduleTimeRange) {
        const [startTime, endTime] = scheduleTimeRange;
        const result = await getSchedules({ startTime, endTime });
        return result.list;
      }
      return [];
    },
    {
      cacheKey: 'SingleDaySchedules',
      debounceWait: 300,
      refreshDeps: [scheduleTimeRange],
    }
  );

  const dataSource = useMemo(() => {
    return scheduleUtil.getDaySchedules(data, dayjs(currentDate));
  }, [currentDate, data]);

  useEffect(() => {
    const handleCreateSuccess = () => {
      refresh();
    };
    window.addEventListener(ScheduleListRefreshEventName, handleCreateSuccess);
    return () => {
      window.removeEventListener(ScheduleListRefreshEventName, handleCreateSuccess);
    };
  }, [refresh]);

  const handleCalendarChange: CalendarProps<dayjs.Dayjs>['onChange'] = (date) => {
    setCurrentDate(date.format('YYYY-MM-DD'));
    setCurrentMonth(date.format('YYYY-MM'));
  };

  const headerRender: CalendarProps<dayjs.Dayjs>['headerRender'] = ({ value, onChange }) => {
    const years = (() => {
      const count = 10;
      const from = value.year() - Math.ceil(count / 2);
      return new Array(count).fill(0).map((_, index) => from + index);
    })();
    const months = new Array(12).fill(null).map((_, index) => index);
    return (
      <ConfigProvider theme={{ token: { lineWidth: 1 } }}>
        <div>
          <div className="px-6 h-8 flex justify-end items-center">
            <Select
              placeholder="年份"
              size="small"
              className="w-24 text-center"
              value={value.year()}
              options={years.map(item => ({ label: `${item}年`, value: item }))}
              onChange={year => onChange(dayjs(value).year(year))}
            />
            <Select
              placeholder="月份"
              size="small"
              className="w-20 text-center ml-2"
              value={value.month()}
              options={months.map(item => ({ label: `${item + 1}月`, value: item }))}
              onChange={month => onChange(dayjs(value).month(month))}
            />
          </div>
          <Divider className="my-4" />
        </div>
      </ConfigProvider>
    );
  };

  const cellRender: CalendarProps<dayjs.Dayjs>['cellRender'] = (date) => {
    if (!calendars) return null;
    const listData = scheduleUtil.getDaySchedules(data, date);
    const badges: JSX.Element[] = [];
    calendars.forEach(item => {
      if (listData?.some(it => item.ID === (it.calendarID || it.calendar?.ID))) {
        const color = item.color || calendarConfig.defaultColor;
        badges.push(
          <Badge
            key={item.ID}
            color={chroma(color).saturate(1.5).hex()}
            size="small"
          />
        );
      }
    });
    return (
      <div className="absolute top-4 w-full flex justify-center">
        {badges}
      </div>
    );
  };

  return (
    <div>
      <ConfigProvider
        theme={{
          token: {
            lineWidth: 0,
          }
        }}
      >
        <Calendar
          mode="month"
          fullscreen={false}
          value={dayjs(currentDate)}
          headerRender={headerRender}
          cellRender={cellRender}
          onChange={handleCalendarChange}
        />
      </ConfigProvider>
      <Divider className="my-4" />
      <ThSpinWithEmpty
        minHeight={140}
        loading={loading}
        empty={{
          image: Empty.PRESENTED_IMAGE_SIMPLE,
          description: loading ? '数据加载中' : '暂无数据',
          styles: { image: { height: 64 } },
        }}
        active={dataSource && dataSource.length > 0}
      >
        <List
          className="px-2 pb-4"
          size="small"
          rowKey="ID"
          bordered={false}
          dataSource={dataSource}
          renderItem={(schedule) => {
            const calendar = calendars?.find(item => {
              const id = schedule.calendarID || schedule.calendar?.ID;
              return id && item.ID === id;
            });
            const color = calendar?.color || calendarConfig.defaultColor;
            return (
              <SchedulePopover
                schedule={schedule}
                date={dayjs(currentDate)}
              >
                <div
                  className="my-2 px-2 py-2 border-0 border-l-2 border-solid cursor-pointer truncate"
                  style={{ backgroundColor: color, borderLeftColor: chroma(color).saturate(1.5).hex() }}
                >
                  <Space size="small">
                    {!scheduleUtil.isAllDay(schedule) && (
                      <span>
                        {dayjs.unix(schedule.startTime).format('HH:mm')}
                      </span>
                    )}
                    <span title={schedule.summary}>
                      {schedule.summary}
                    </span>
                  </Space>
                </div>
              </SchedulePopover>
            )
          }}
        />
      </ThSpinWithEmpty>
    </div>
  );
};

export default MiniSchedule;
